// Сводка за период: четыре числа, которые финансовый директор видит, ничего не нажав.
//
// Живёт в правой колонке, когда строка не выбрана. Это не заглушка «выберите человека», а
// настоящий экран: открыл раздел — слева ранжированный список, справа четыре числа, ноль
// кликов. Выбрал человека — сводка уступает место карточке.
//
// Полосой во всю ширину эти числа не кладутся намеренно: это была бы новая поверхность
// поперёк экрана, которой нет в скелете раздела (правило «не изобретать поверхностей»).

import { useState } from 'react';

import type { DashboardOut } from '@/shared/api/endpoints/accountable';
import type { CashTrendOut } from '@/shared/api/endpoints/reports';
import { formatDateOnly, formatLastDayBefore } from '@/shared/time/format';
import { pluralRu } from '@/shared/text/plural';
import { ДавностьПодотчёта } from '@/shared/reports/ДавностьПодотчёта';
import { ДеньгиВПолях } from '@/shared/reports/ДеньгиВПолях';

import { EMPTY_SEARCH, buildSearch } from '@/shared/routing/search';
import { buildPath } from '@/shared/routing/paths';
import {
  FILTER_FOCUS,
  FILTER_LEGAL_ENTITY,
  FILTER_ONLY_OPEN,
  FILTER_PERIOD,
  FOCUS_STAFFING,
  ONLY_OPEN,
  VIEW_DEBTS,
  VIEW_SHEETS,
  VIEW_SUMMARY,
  VIEW_TIMESHEET,
} from '@/modules/payroll/route';

import { KpiTile } from './KpiTile';
import { РазборПодотчёта } from './РазборПодотчёта';

/** «2026-07-31» → «2026-07»: месяц в адресе кодируется парой «год-месяц» (`shared/time/period`). */
function месяцПериода(дата: string): string {
  return дата.slice(0, 7);
}

export interface PeriodSummaryProps {
  data: DashboardOut;
  /** Отбор по юрлицу, действующий на пульте. Уезжает вместе с каждым переходом. */
  legalEntityId?: string;
  /**
   * Движение подотчёта по дням. Едет отдельным запросом и может ещё не приехать: полоса
   * появляется, когда данные есть, и не держит собой остальную сводку.
   */
  trend?: CashTrendOut;
}

export function PeriodSummary({ data, trend, legalEntityId }: PeriodSummaryProps) {
  // Разбор раскрывается ЗДЕСЬ ЖЕ, а не уводит: плитка, ведущая на текущий экран, и означает,
  // что сводка и её разрез — одно место (см. `РазборПодотчёта`).
  const [разбор, setРазбор] = useState(false);
  const людей = data.people_with_balance.count ?? 0;
  // Месяц и юрлицо ОБЯЗАНЫ уехать вместе с переходом. Без них человек, смотрящий июль,
  // нажимал «Подробно» и попадал на текущий месяц по всем юрлицам — то есть на другие числа,
  // ничем не похожие на те, из-за которых он и нажал.
  const разрез = (view: string, filters: Record<string, string> = {}): string =>
    buildPath({ section: 'payroll', view }) +
    buildSearch({
      ...EMPTY_SEARCH,
      filters: {
        [FILTER_PERIOD]: месяцПериода(data.period_from),
        ...(legalEntityId === undefined ? {} : { [FILTER_LEGAL_ENTITY]: legalEntityId }),
        ...filters,
      },
    });
  // Перечисляем только неизвестное. Показатель, который посчитался, стоит плиткой и в этом
  // списке ему делать нечего.
  const нехватка = [data.debts_to_withhold, data.understaffed_hours]
    .filter((показатель) => показатель.state === 'unknown')
    .map((показатель) => [показатель.reason, показатель.expected_from].filter(Boolean).join(': '))
    .filter((строка) => строка.length > 0);
  return (
    <div className='flex flex-col gap-5 p-5'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-label-lg text-text-strong-950'>Сводка за период</h2>
        {/* Правило 9: календарь подписан датами, а не только названием месяца. */}
        <p className='text-paragraph-sm text-text-sub-600'>
          {data.period_title} · {formatDateOnly(data.period_from)} —{' '}
          {formatLastDayBefore(data.period_to)}
        </p>
      </div>

      <div className='grid grid-cols-2 gap-4'>
        {/* Каждый показатель ведёт в свой разрез (сценарий 57): число без ответа на вопрос
            «из чего оно» — тупик, из которого человек идёт спрашивать в чат. */}
        <KpiTile
          title='Неподтверждённый подотчёт'
          value={data.unconfirmed_total}
          footnote={
            людей > 0
              ? `у ${людей} ${pluralRu(людей, ['человека', 'человек', 'человек'])}`
              : undefined
          }
          onOpen={() => setРазбор((было) => !было)}
          открыто={разбор}
        />
        {/* Две из четырёх величин периодом НЕ ограничены, и это сказано подписью. Заголовок
            блока обещает «за период» и подписан датами, поэтому молчание здесь читалось бы
            как «долгов за август столько-то» — а это остаток за всё время, и решение по нему
            другое. */}
        {/* Реестр долгов сделан ровно для этого вопроса. До 21.09.2026 плитка вела на
            общую сводку «Расчёта» — туда же, куда и соседняя, — и из слова «Подробно» было
            не понять, что именно откроется. */}
        <KpiTile
          title='Долги сотрудников'
          value={data.debts_total}
          footnote='действующие, за всё время'
          href={разрез(VIEW_DEBTS, { [FILTER_ONLY_OPEN]: ONLY_OPEN })}
        />
        <KpiTile
          title='Объекты с недобором'
          value={data.understaffed_sites}
          footnote='на сегодня, по активным постам'
          href={разрез(VIEW_SUMMARY, { [FILTER_FOCUS]: FOCUS_STAFFING })}
        />
        <KpiTile
          title='К выплате за период'
          value={data.payable_total}
          href={разрез(VIEW_SUMMARY)}
        />
        {/* Пятая плитка появляется, только когда число есть: пустая плитка «неизвестно» рядом
            с четырьмя живыми читается как поломка, а её объяснение и так стоит ниже. */}
        {data.debts_to_withhold.state !== 'unknown' && (
          <KpiTile
            title='Удержится в этом расчёте'
            value={data.debts_to_withhold}
            footnote='по посчитанным ведомостям периода'
            href={разрез(VIEW_SHEETS)}
          />
        )}
        {/* Недобор в часах. Минус здесь законен и означает переработку — об этом сказано
            подписью, иначе отрицательное число читается как ошибка расчёта. */}
        {data.understaffed_hours.state !== 'unknown' && (
          <KpiTile
            title='Недобор в часах'
            value={data.understaffed_hours}
            footnote='по табелям месяца; со знаком минус — переработка'
            href={разрез(VIEW_TIMESHEET)}
          />
        )}
      </div>

      {/* Разбор первой плитки. Стоит СРАЗУ под сеткой, а не в конце экрана: человек нажал
          на число и смотрит на него, а не ищет, где внизу что-то появилось. */}
      {разбор && (
        <РазборПодотчёта legalEntityId={legalEntityId} total={data.unconfirmed_total.amount ?? 0} />
      )}

      {/* Давность. Плитка сверху отвечает «сколько висит», и на этом экран замолкал: под
          шестью плитками оставалось полтысячи точек пустоты, а второй вопрос — «давно ли» —
          был виден только по одному человеку за раз, строкой в списке слева.

          Показывается, только когда есть чему делиться: при закрытом подотчёте разбивка
          приходит пустой, а причину словами уже назвала сама плитка. Второй раз объяснять
          одно и то же нельзя — правило «одно сообщение, а не два разными словами». */}
      {/* Читается защищённо. Кабинет показывает данные ПРОШЛОЙ сессии на нулевой миллисекунде
          (правило 11), а сохранённый ответ мог быть снят до появления этого поля — на
          обновлении программы у человека упал бы весь экран, а не одна карточка. */}
      {(data.unconfirmed_by_age ?? []).length > 0 && (data.unconfirmed_total.amount ?? 0) > 0 && (
        <div className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
          <ДавностьПодотчёта
            buckets={data.unconfirmed_by_age ?? []}
            total={data.unconfirmed_total.amount ?? 0}
          />
        </div>
      )}

      {/* Растёт подотчёт или тает. Календарём эта полоса не управляется: она всегда про
          последние тридцать дней, и подписана этим прямо.

          Полоса появляется, только когда за тридцать дней было движение, — тем же условием, что
          в карточке человека и на «На что потрачено». Сервер отдаёт тридцать дней всегда, нулями в
          том числе, и до 06.10.2026 после очистки данных пульт рисовал ровную линию с выводом
          «Сколько выдали, столько и вернули с отчётами» — про движение, которого не было. */}
      {trend !== undefined &&
        trend.items.length > 0 &&
        (trend.total_issued > 0 || trend.total_returned > 0 || trend.total_spent > 0) && (
          <div className='rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
            <ДеньгиВПолях data={trend} />
          </div>
        )}

      {/* Чего ещё не хватает — ТОЛЬКО про то, чего правда нет.
          
          До 03.09.2026 блок печатал две строки безусловно, потому что оба показателя были
          «неизвестно» всегда. Когда они стали считаться, строки остались бы на экране с
          пустотой вместо причины — и человек читал бы «сколько удержится, считает ведомость:»
          с обрывом на двоеточии.
          
          Белая карточка с рамкой, а не серая заливка: фон правой колонки САМ серый, и на нём
          серая плашка не читалась как отдельный блок — на живом экране это выглядело просто
          текстом, повисшим под плитками. */}
      {нехватка.length > 0 && (
        <div className='flex flex-col gap-2 rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-4'>
          <span className='text-label-sm text-text-strong-950'>Чего ещё не хватает</span>
          <ul className='flex flex-col gap-1 text-paragraph-sm text-text-sub-600'>
            {нехватка.map((строка) => (
              <li key={строка}>{строка}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
